import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getRiders, createRider, updateRider, deleteRider } from '../services/api';
import Icon from './Icon';
import './RidersManager.css';

const RIDER_STATUSES = ['available', 'busy', 'offline'];
const EMPTY_FORM = { name: '', phone: '', status: 'offline' };

/**
 * Gestión CRUD de repartidores propios (own delivery).
 * Persiste inmediatamente contra los endpoints /restaurants/{id}/riders
 * (independiente del guardado de settings).
 */
function RidersManager({ restaurantId }) {
  const { t } = useTranslation();
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const loadRiders = useCallback(async () => {
    if (!restaurantId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const data = await getRiders(restaurantId);
      setRiders(data?.riders || []);
    } catch (err) {
      console.error('Error loading riders:', err);
      setError(t('orderModes.riders.loadError'));
    } finally {
      setLoading(false);
    }
  }, [restaurantId, t]);

  useEffect(() => {
    loadRiders();
  }, [loadRiders]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (rider) => {
    setEditing(rider);
    setForm({ name: rider.name, phone: rider.phone, status: rider.status });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setFormError(t('orderModes.riders.requiredFields'));
      return;
    }
    try {
      setSaving(true);
      setFormError('');
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        status: form.status,
      };
      if (editing) {
        await updateRider(restaurantId, editing.riderId, payload);
      } else {
        await createRider(restaurantId, payload);
      }
      await loadRiders();
      closeModal();
    } catch (err) {
      console.error('Error saving rider:', err);
      setFormError(t('orderModes.riders.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (rider, status) => {
    // Optimistic update
    setRiders((prev) => prev.map((r) => (r.riderId === rider.riderId ? { ...r, status } : r)));
    try {
      await updateRider(restaurantId, rider.riderId, { status });
    } catch (err) {
      console.error('Error updating rider status:', err);
      setError(t('orderModes.riders.saveError'));
      loadRiders();
    }
  };

  const handleDelete = async (rider) => {
    if (!window.confirm(t('orderModes.riders.deleteConfirm', { name: rider.name }))) return;
    try {
      await deleteRider(restaurantId, rider.riderId);
      setRiders((prev) => prev.filter((r) => r.riderId !== rider.riderId));
    } catch (err) {
      console.error('Error deleting rider:', err);
      setError(t('orderModes.riders.deleteError'));
    }
  };

  return (
    <div className="riders-manager">
      <div className="riders-header">
        <h4>{t('orderModes.riders.title')}</h4>
        <button type="button" className="btn btn-secondary btn-sm" onClick={openCreate}>
          <Icon name="add" />
          {t('orderModes.riders.add')}
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <Icon name="error" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <Icon name="hourglass_empty" />
          <span>{t('common.loading')}</span>
        </div>
      ) : riders.length === 0 ? (
        <p className="riders-empty">{t('orderModes.riders.empty')}</p>
      ) : (
        <ul className="riders-list">
          {riders.map((rider) => (
            <li key={rider.riderId} className="rider-item">
              <div className="rider-info">
                <span className={`rider-status-dot status-${rider.status}`} aria-hidden="true" />
                <div className="rider-text">
                  <span className="rider-name">{rider.name}</span>
                  <span className="rider-phone">{rider.phone}</span>
                </div>
              </div>
              <div className="rider-actions">
                <select
                  className="time-select rider-status-select"
                  value={rider.status}
                  aria-label={t('orderModes.riders.statusLabel')}
                  onChange={(e) => handleStatusChange(rider, e.target.value)}
                >
                  {RIDER_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {t(`orderModes.riders.statuses.${s}`)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm icon-btn"
                  aria-label={t('common.edit')}
                  onClick={() => openEdit(rider)}
                >
                  <Icon name="edit" />
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm icon-btn"
                  aria-label={t('common.delete')}
                  onClick={() => handleDelete(rider)}
                >
                  <Icon name="delete" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editing ? t('orderModes.riders.editTitle') : t('orderModes.riders.addTitle')}
              </h2>
              <button type="button" className="close-btn" onClick={closeModal}>
                <Icon name="close" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="rider-name">{t('orderModes.riders.name')}</label>
                  <input
                    id="rider-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="rider-phone">{t('orderModes.riders.phone')}</label>
                  <input
                    id="rider-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="rider-status">{t('orderModes.riders.statusLabel')}</label>
                  <select
                    id="rider-status"
                    className="time-select"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    {RIDER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(`orderModes.riders.statuses.${s}`)}
                      </option>
                    ))}
                  </select>
                </div>
                {formError && <div className="error-message">{formError}</div>}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={closeModal}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? t('common.loading') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default RidersManager;
